import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import FAQ from "../components/blocks/FAQ";
import { TextCard, LinkCard, ProgrammeCard, FrameworkCard } from "../components/blocks/Cards";
import { WHY_INSTITUTE, AUDIENCES, DOMAINS, PILLARS, ORG_CAPABILITIES, KNOWLEDGE_CATEGORIES, HOME_FAQS } from "../data/content";
import { PROGRAMMES } from "../data/programmes";
import { FRAMEWORKS } from "../data/frameworks";

export default function HomePage() {
  const featured = PROGRAMMES.filter((p) => p.featured).slice(0, 4);
  return (
    <>
      <SEO
        title="The Vantage Institute™ — Executive Education • Research • Intellectual Capital"
        description="The Vantage Institute develops strategic thinking, leadership capability, business skills and practical judgement for leaders, professionals, entrepreneurs and organisations."
        path="/"
      />

      {/* HERO */}
      <section className="bg-navy" data-testid="hero">
        <div className="container-x py-20 sm:py-28 lg:py-32">
          <div className="max-w-4xl">
            <p className="eyebrow-light animate-fade-up">Executive Education • Research • Intellectual Capital</p>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.15] text-white animate-fade-up sm:text-5xl lg:text-6xl">
              Think Better. Lead Better. Perform Better. Transform Better.
            </h1>
            <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum animate-fade-up md:text-lg">
              The Vantage Institute develops the strategic thinking, leadership capability, business skills and
              practical judgement leaders need to perform, grow and transform in a changing business environment.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="/learning" className="btn-gold" data-testid="hero-cta-find-path">Find Your Learning Path</Link>
              <Link to="/programmes" className="btn-outline-light" data-testid="hero-cta-programmes">Explore Programmes</Link>
            </div>
            <p className="mt-6 text-sm text-platinum/80">
              Not sure where to start?{" "}
              <Link to="/assessment" className="font-sub font-semibold uppercase tracking-wide text-gold underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold-soft" data-testid="hero-cta-assessment">
                Take the Capability Assessment
              </Link>
            </p>
            <p className="mt-8 text-sm text-platinum/80">
              Building capability for your organisation?{" "}
              <Link to="/organisations" className="font-sub font-semibold uppercase tracking-wide text-gold underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold-soft" data-testid="hero-cta-organisations">
                For Organisations
              </Link>
            </p>
            <p className="mt-3 text-sm text-platinum/80">
              Leading a founder-led or growth-stage business?{" "}
              <Link to="/advisory" className="font-sub font-semibold uppercase tracking-wide text-gold underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold-soft" data-testid="hero-cta-advisory">
                For Founders — Vantage Advisory
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* WHY VANTAGE INSTITUTE */}
      <section className="container-x py-16 sm:py-24" data-testid="why-institute">
        <SectionHeading
          eyebrow="Why Vantage Institute?"
          title="Not a training provider. An institution for capability."
          intro="Most providers deliver courses. The Vantage Institute brings together six disciplines that, in combination, build something more durable than knowledge: the capability to think, decide and lead."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_INSTITUTE.map((item) => (
            <TextCard key={item.title} title={item.title} text={item.text} testId={`why-${item.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} />
          ))}
        </div>
      </section>

      {/* FIND YOUR LEARNING PATH */}
      <section className="bg-paper" data-testid="learning-paths">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            eyebrow="Find Your Learning Path"
            title="Start from where you stand."
            intro="Every Vantage learning journey begins with who you are and what you are responsible for. Choose the path built for your stage."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a) => (
              <LinkCard
                key={a.title}
                title={a.title}
                text={a.text}
                to={a.to || "/learning"}
                linkLabel={a.to ? "For Organisations" : "Explore Path"}
                testId={`audience-${a.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITY DOMAINS */}
      <section className="container-x py-16 sm:py-24" data-testid="capability-domains">
        <SectionHeading
          eyebrow="Capability Domains"
          title="What you can develop at Vantage."
          intro="The Institute's curriculum spans eight capability domains — the full arc of what modern leaders and organisations must be good at."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {DOMAINS.map((d) => (
            <LinkCard key={d.title} title={d.title} text={d.text} to="/programmes" linkLabel="View Programmes" testId={`domain-${d.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} />
          ))}
        </div>
      </section>

      {/* THINKING TOOLBOX */}
      <section className="bg-navy" data-testid="thinking-toolbox">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            light
            eyebrow="The Vantage Thinking Toolbox™"
            title="Better thinking is a trainable discipline."
            intro="The Toolbox is the Institute's proprietary thinking platform: thirteen frameworks that improve the quality of judgement behind every business decision. It is the intellectual backbone of every Vantage programme."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FRAMEWORKS.map((f) => (
              <div key={f.slug} className="border border-white/15 bg-navy-light/40 p-6 transition-colors duration-200 hover:border-gold">
                <h3 className="font-sub text-base font-semibold text-white">{f.name}</h3>
                <p className="mt-1.5 text-sm italic text-gold">{f.tagline}</p>
                {f.detailed && (
                  <Link to={`/thinking-toolbox/${f.slug}`} className="mt-4 inline-flex min-h-[44px] items-center font-sub text-xs font-semibold uppercase tracking-[0.12em] text-gold transition-colors hover:text-gold-soft" data-testid={`home-framework-${f.slug}`}>
                    Explore Framework
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/thinking-toolbox" className="btn-gold" data-testid="home-cta-toolbox">Explore the Thinking Toolbox™</Link>
          </div>
        </div>
      </section>

      {/* FEATURED PROGRAMMES */}
      <section className="container-x py-16 sm:py-24" data-testid="featured-programmes">
        <SectionHeading
          eyebrow="Featured Executive Programmes"
          title="Programmes built for real responsibility."
          intro="A selection from the Vantage catalogue. Full curricula, schedules and enrolment details are being finalised — register your interest to be notified."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p) => (
            <ProgrammeCard key={p.slug} programme={p} testId={`featured-${p.slug}`} />
          ))}
        </div>
        <div className="mt-10">
          <Link to="/programmes" className="btn-outline-navy" data-testid="home-cta-all-programmes">Explore All Programmes</Link>
        </div>
      </section>

      {/* FOR ORGANISATIONS */}
      <section className="bg-paper" data-testid="for-organisations">
        <div className="container-x py-16 sm:py-24">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <SectionHeading
              eyebrow="For Organisations"
              title="Build Capability That Moves the Business."
              intro="Vantage designs corporate learning around one test: does it change what the business can do? Solutions are built with your context, your audience and your desired outcome at the centre."
            />
            <div>
              <ul className="grid gap-3 sm:grid-cols-2">
                {ORG_CAPABILITIES.map((c) => (
                  <li key={c} className="flex items-center gap-3 border border-platinum bg-white px-4 py-3.5 font-sub text-sm font-semibold text-navy">
                    <span className="h-px w-6 shrink-0 bg-gold" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
              <Link to="/organisations#enquiry" className="btn-navy mt-8" data-testid="home-cta-corporate">
                Request a Corporate Conversation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* KNOWLEDGE HUB */}
      <section className="container-x py-16 sm:py-24" data-testid="knowledge-hub">
        <SectionHeading
          eyebrow="Knowledge Hub"
          title="Intellectual capital, in the open."
          intro="Insights, frameworks, research and practical tools from the Institute — built to be read in minutes and used for years."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {KNOWLEDGE_CATEGORIES.slice(0, 5).map((k) => (
            <LinkCard key={k.title} title={k.title} text={k.text} to="/knowledge" linkLabel="Explore" testId={`knowledge-${k.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} />
          ))}
        </div>
        <div className="mt-10">
          <Link to="/knowledge" className="btn-outline-navy" data-testid="home-cta-knowledge">Explore Knowledge</Link>
        </div>
      </section>

      {/* WHY VANTAGE — PILLARS */}
      <section className="border-t border-platinum bg-white" data-testid="why-vantage">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            eyebrow="Why Vantage"
            title="Four commitments behind everything we build."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="border-t-2 border-gold pt-6" data-testid={`pillar-${p.title.toLowerCase()}`}>
                <p className="font-display text-3xl font-semibold text-platinum" aria-hidden="true">0{i + 1}</p>
                <h3 className="mt-3 font-sub text-base font-semibold text-navy">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQ items={HOME_FAQS} title="Questions, Answered Directly" />

      {/* FINAL CTA */}
      <CTASection
        title="Build the capabilities that your next stage of growth demands."
        text="Whether you are leading yourself, a team or an enterprise — the next stage requires new capability. Vantage exists to build it."
        primaryLabel="Find Your Learning Path"
        primaryTo="/learning"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="final-cta"
      />
    </>
  );
}
